'use client';

import { useEffect, useRef } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { BASE_PATH } from '@/app/components/search/utils/path';
import { URLParameters } from '@/app/common/urlParameters';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { SearchModes } from '@/domainLogic/index.ts';
import { useInitialParams } from '../InitialParamsProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { useOffersContext } from '../OffersProvider';
import { buildAccommodationInterval } from '../common/common.helper';
import { fetchWithTracingAndLogging } from '../common/fetchWithTracing';
import { getLoggingInfo } from '../lib/loggingInfo';

// yyyyMMdd -> yyyy-MM-dd (matches how useFetchRooms formats dates for the backend).
const toIsoDate = (date: string) => date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');

/**
 * When the detail page determines a hotel has no available rooms, notify the Apex realtime-processor
 * so it can mark the hotel as "No Results". Mounted in both the EVO (`EvoDetailsLayout`) and legacy
 * (`HotelAndRoomsLayout`) detail layouts. Fire-and-forget: gated solely by the
 * `apexRealtimeNoAvailabilityEnabled` toggle, fired once per (hotel + search key), response ignored,
 * never blocks or changes the UI.
 */
export function useReportHotelNoAvailability() {
  const { internalHotelId, pageType } = useInitialParams();
  const { checkin, checkout, adults, travellerGroups } = useUrlSearchParams();
  const { offersData } = useOffersContext();
  const { commonData, searchInfoData, localization } = usePageWrapperData();
  const { apexRealtimeNoAvailabilityEnabled } = localization.configValuesApp;

  // Report only a genuine sold-out (retries exhausted, no bookable rooms). `noAvailableRooms` is also
  // set to true for transient failures — ERROR_FETCHING_ROOMS, ERROR_PRICING_ASYNC,
  // ERROR_PROCESSING_ROOMS_RESPONSE (see OffersProvider) — so gating on it would tell Apex to mark an
  // available hotel as "No Results" during a rooms/pricing blip. Gate on the terminal ROOM_NOT_AVAILABLE
  // response code instead, which only OffersProvider's exhausted-retries path sets.
  // The sold-out must also belong to THIS hotel: offersData survives similar-hotel detail->detail
  // navigation, so without the id match a stale sold-out from the previous hotel would be reported
  // against the new hotel before its own rooms response arrives.
  const hotelSoldOut =
    offersData?.responseCode === 'ROOM_NOT_AVAILABLE' && offersData?.internalHotelId === internalHotelId;
  // A Set, not a single last-key slot: alternating between sold-out stays (A -> B -> A) re-fires
  // this effect with the SWR-cached rooms error, and A must not be re-POSTed with identical params.
  const reportedKeys = useRef<Set<string>>(new Set());

  // When the user picked partial hotel-stay dates on the results page (shorter than the flight
  // interval, stored in `prefilter_checkin_checkout`), report those to Apex instead of the flight
  // dates — the no-availability is about the hotel stay, not the trip. Already yyyy-MM-dd, so no
  // conversion; fall back to the trip dates (yyyyMMdd) when there is no partial stay.
  const accommodation = buildAccommodationInterval(
    getSearchParams()[URLParameters.CUSTOM_CHECKIN_CHECKOUT],
    searchInfoData.searchMode === SearchModes.DP
  );
  const reportCheckin = accommodation?.start.date ?? toIsoDate(checkin);
  const reportCheckout = accommodation?.end.date ?? toIsoDate(checkout);

  useEffect(() => {
    if (!apexRealtimeNoAvailabilityEnabled || !hotelSoldOut) return;

    // Apex has no multi-room concept: flatten children ages across rooms into one list.
    const childrenAges = travellerGroups.flatMap((group) => group.childrenAges);
    const key = `${internalHotelId}|${reportCheckin}|${reportCheckout}|${adults}|${childrenAges.join(',')}`;
    if (reportedKeys.current.has(key)) return; // report once per (hotel + search key)
    reportedKeys.current.add(key);

    fetchWithTracingAndLogging(
      `${BASE_PATH}/api/hotel-detail/hotel-no-availability`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          hotelId: internalHotelId,
          checkin: reportCheckin,
          checkout: reportCheckout,
          adults,
          children_ages: childrenAges,
          // Availability is BP-scoped in Apex: tell it which business profile saw no rooms so it
          // marks "No Results" for the right profile only.
          businessProfile: localization.businessProfile,
        }),
      },
      getLoggingInfo(pageType, 'apex-hotel-no-availability', 'HotelPageRoomSection'),
      searchInfoData.searchTraceParent,
      commonData
    ).catch(() => {}); // fire-and-forget: best effort
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelSoldOut, apexRealtimeNoAvailabilityEnabled, internalHotelId, reportCheckin, reportCheckout, adults]);
}
